/* eslint-disable */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/*import url and gateway methods */
import { getData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import crmLeadFieldList from "data/Prod/CrmLeadFieldList.json";

const CrmLeadAllowFieldsBySector = ({
  moduleSlug = "MOD_LEAD",
  setShowAlert = () => {},
  setAlertMessage = () => {},
  setMessageType = () => {},
}) => {
  const { t } = useTranslation();

  const allFieldValues = crmLeadFieldList.map((item) => item.value);

  const [selectedFields, setSelectedFields] = useState(allFieldValues);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  //group fields the same way the lead form accordions are organised
  const groupedFields = crmLeadFieldList.reduce((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = [];
    }
    acc[item.group].push(item);
    return acc;
  }, {});

  //fetch currently allowed fields for this module in this ecosystem
  const getFieldPrivilegeDetails = async () => {
    setIsLoading(true);
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_FIELD_PRIVILEGE +
        `?module=${moduleSlug}`;

      const response = await getData(requestUrl);

      if (response.status && response.data && response.data.fields) {
        setSelectedFields(response.data.fields);
      } else {
        setSelectedFields(allFieldValues);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  //toggle a single field checkbox
  const fieldToggleHandler = (fieldValue) => {
    setSelectedFields((prev) =>
      prev.includes(fieldValue)
        ? prev.filter((item) => item !== fieldValue)
        : [...prev, fieldValue],
    );
  };

  //whether every field of a section is currently allowed
  const isSectionAllowed = (groupFields) =>
    groupFields.every((field) => selectedFields.includes(field.value));

  //toggle every field of a section together - enables/disables the whole section in one click
  const sectionToggleHandler = (groupFields) => {
    const groupValues = groupFields.map((field) => field.value);
    const isFullyAllowed = groupValues.every((value) =>
      selectedFields.includes(value),
    );

    setSelectedFields((prev) =>
      isFullyAllowed
        ? prev.filter((value) => !groupValues.includes(value))
        : [...new Set([...prev, ...groupValues])],
    );
  };

  //save allowed fields for this module in this ecosystem
  const saveFieldPrivilegeHandler = async () => {
    setIsSaving(true);
    try {
      const fieldPrivilegeData = {
        module: moduleSlug,
        fields: selectedFields,
      };

      const requestUrl = url.API_BASE_URL + url.API_SAVE_FIELD_PRIVILEGE;

      const response = await postData(requestUrl, fieldPrivilegeData);

      setMessageType(response.status ? "success" : "error");
      setAlertMessage(response.message);
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
    } finally {
      setIsSaving(false);
      setShowAlert(true);
    }
  };

  //fetch fresh data every time the offcanvas is opened
  useEffect(() => {
    const offcanvasElement = document.getElementById(
      "crmLeadAllowFieldsBySector",
    );

    if (offcanvasElement) {
      offcanvasElement.addEventListener(
        "show.bs.offcanvas",
        getFieldPrivilegeDetails,
      );
    }

    return () => {
      if (offcanvasElement) {
        offcanvasElement.removeEventListener(
          "show.bs.offcanvas",
          getFieldPrivilegeDetails,
        );
      }
    };
  }, [moduleSlug]);

  return (
    <div
      className="offcanvas offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="crmLeadAllowFieldsBySector"
      aria-labelledby="crmLeadAllowFieldsBySectorLabel"
      style={{ display: "flex", flexDirection: "column", height: "100vh" }}
    >
      <div className="offcanvas-header bg-primary text-white p-4 border-bottom border-gray-300">
        <h5
          className="offcanvas-title fw-bold"
          id="crmLeadAllowFieldsBySectorLabel"
        >
          {t("Allowed Fields")}
        </h5>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>

      <div
        className="offcanvas-body p-4"
        style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          Object.keys(groupedFields).map((groupName) => {
            const groupFields = groupedFields[groupName];
            const sectionAllowed = isSectionAllowed(groupFields);
            return (
            <div key={groupName} className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold text-primary mb-0">{t(groupName)}</h6>
                <button
                  type="button"
                  className={`d-flex align-items-center gap-1 border-0 bg-transparent px-1 fs-xs fw-semibold ${
                    sectionAllowed ? "text-primary" : "text-gray-500"
                  }`}
                  onClick={() => sectionToggleHandler(groupFields)}
                  title={
                    sectionAllowed
                      ? t("Disable whole section")
                      : t("Enable whole section")
                  }
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "20px" }}
                  >
                    {sectionAllowed ? "toggle_on" : "toggle_off"}
                  </span>
                  {sectionAllowed ? t("Section Enabled") : t("Section Disabled")}
                </button>
              </div>
              <ul
                className={`list-unstyled mb-0 border rounded-3 overflow-hidden ${
                  !sectionAllowed ? "opacity-50" : ""
                }`}
              >
                {groupFields.map((field, index) => {
                  const isAllowed = selectedFields.includes(field.value);
                  return (
                    <li
                      key={field.value}
                      className={`d-flex align-items-center justify-content-between px-3 py-2 ${
                        index !== groupedFields[groupName].length - 1
                          ? "border-bottom"
                          : ""
                      }`}
                    >
                      <span className="fs-sm">{t(field.label)}</span>
                      <button
                        type="button"
                        className={`d-flex align-items-center justify-content-center rounded-circle border p-0 ${
                          isAllowed
                            ? "bg-primary border-primary"
                            : "bg-white border-gray-300"
                        }`}
                        style={{ width: "26px", height: "26px" }}
                        onClick={() => fieldToggleHandler(field.value)}
                        aria-pressed={isAllowed}
                        title={isAllowed ? t("Allowed") : t("Not Allowed")}
                      >
                        {isAllowed && (
                          <span
                            className="material-symbols-outlined text-white"
                            style={{ fontSize: "16px" }}
                          >
                            check
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })
        )}
      </div>

      <div className="offcanvas-footer p-4 bg-white border-top border-gray-300 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary px-4 d-flex align-items-center gap-2 shadow"
          onClick={saveFieldPrivilegeHandler}
          disabled={isSaving || isLoading}
          style={{ cursor: isSaving || isLoading ? "not-allowed" : "pointer" }}
        >
          {isSaving && (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {t("Save")}
        </button>
      </div>
    </div>
  );
};

export default CrmLeadAllowFieldsBySector;
